import {
  BRANDING,
  sanitizeErpBranding,
} from "app/utils/branding";

import {
  createNetworkError,
  createOdooApiError,
  OdooApiError,
} from "app/utils/apiErrors";
import {
  clearDeliveryDashboardSettings,
  syncDeliveryDashboardSettings,
} from "app/utils/deliveryDashboardSettings";

export { formatOdooApiErrorMessage, OdooApiError } from "app/utils/apiErrors";

export const AUTH_STORAGE_KEY = "auth_data";

export const AUTH_REQUEST = "DYN_DELIVERY_DASHBOARD_REQUEST_AUTH";
export const AUTH_RESPONSE = "DYN_DELIVERY_DASHBOARD_AUTH";
export const AUTH_ERROR = "DYN_DELIVERY_DASHBOARD_AUTH_ERROR";
export const AUTH_DATA_UPDATED_EVENT = "dyn_delivery_dashboard_auth_updated";

export interface UserContext {
  tz?: string;
  lang?: string;
  uid?: number;
  [key: string]: unknown;
}

export interface AuthData {
  access_token?: string;
  refresh_token?: string;
  uid?: number;
  expires_in?: number;
  tz?: string;
  user_context?: UserContext;
  [key: string]: unknown;
}

export class AuthExpiredError extends Error {
  constructor(message = "Session expired. Please sign in again.") {
    super(message);
    this.name = "AuthExpiredError";
  }
}

let authRecoveryPromise: Promise<AuthData | null> | null = null;
let inMemoryAuthData: AuthData | null = null;

export function isEmbeddedMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get("embedded") === "1") {
    return true;
  }
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function getStoredAuthData(): AuthData | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.access_token) {
        inMemoryAuthData = parsed;
        return parsed;
      }
    }
  } catch {
    // Safari may block storage inside a cross-origin iframe.
  }

  return inMemoryAuthData?.access_token ? inMemoryAuthData : null;
}

export function storeAuthData(authData: AuthData): void {
  inMemoryAuthData = authData;

  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
  } catch {
    // Keep auth in memory when iframe storage is blocked (common on mobile Safari).
  }

  window.dispatchEvent(
    new CustomEvent<AuthData>(AUTH_DATA_UPDATED_EVENT, { detail: authData })
  );
}

export async function completeAuthSession(authData: AuthData): Promise<AuthData> {
  storeAuthData(authData);

  if (authData.access_token) {
    await syncDeliveryDashboardSettings(authData.access_token);
  }

  return authData;
}

export function clearAuthData(): void {
  inMemoryAuthData = null;
  clearDeliveryDashboardSettings();

  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem("partnerId");
  } catch {
    // Ignore storage cleanup failures in restricted iframe contexts.
  }

  window.dispatchEvent(
    new CustomEvent<AuthData | null>(AUTH_DATA_UPDATED_EVENT, { detail: null })
  );
}

export function formatAuthErrorMessage(error: unknown): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Embedded authentication failed.";

  const trimmed = raw.trim();
  if (!trimmed) {
    return "Embedded authentication failed.";
  }

  if (
    trimmed.startsWith("<!doctype") ||
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<html") ||
    trimmed.includes("<title>500 Internal Server Error</title>") ||
    trimmed.includes("Internal Server Error")
  ) {
    return BRANDING.signInServerUnavailable;
  }

  if (
    trimmed.includes("Timed out waiting for System session authentication") ||
    trimmed.includes("Timed out waiting")
  ) {
    return BRANDING.signInTimeout;
  }

  if (trimmed.length > 220) {
    return BRANDING.signInGenericFailure;
  }

  return sanitizeErpBranding(trimmed);
}

export function getOdooApiHeaders(
  accessToken: string,
  contentType = "text/html"
): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": contentType,
  };
}

export function getOdooOrigin(): string | null {
  const params = new URLSearchParams(window.location.search);
  const odooOriginParam = params.get("odoo_origin");
  if (odooOriginParam) {
    try {
      return new URL(odooOriginParam).origin;
    } catch {
      return odooOriginParam.replace(/\/$/, "");
    }
  }

  const configuredOrigin = process.env.REACT_APP_ODOO_ORIGIN;
  if (configuredOrigin) {
    return configuredOrigin.replace(/\/$/, "");
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!apiBaseUrl) {
    return null;
  }

  try {
    const url = new URL(apiBaseUrl);
    return url.origin;
  } catch {
    return null;
  }
}

export function isAllowedOdooOrigin(origin: string): boolean {
  if (!origin || origin === "null") {
    return false;
  }

  const expectedOrigin = getOdooOrigin();
  if (!expectedOrigin) {
    return true;
  }

  if (origin === expectedOrigin) {
    return true;
  }

  if (!isEmbeddedMode()) {
    return false;
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!apiBaseUrl) {
    return false;
  }

  try {
    return origin === new URL(apiBaseUrl).origin;
  } catch {
    return false;
  }
}

export function isInvalidTokenPayload(data: unknown): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }

  const payload = data as { error?: string; error_descrip?: string };
  return (
    payload.error === "invalid_token" ||
    payload.error_descrip === "Token is expired or invalid!"
  );
}

export async function refreshAccessToken(
  refreshToken: string
): Promise<Pick<AuthData, "access_token" | "expires_in">> {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!apiBaseUrl) {
    throw new Error("Missing REACT_APP_API_BASE_URL.");
  }

  const response = await fetch(`${apiBaseUrl}/auth/refresh_token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
      access_lifetime: 172800,
    }),
  });

  const text = await response.text();
  let parsed: AuthData & { error?: string; error_descrip?: string };
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Could not refresh access token.");
  }

  if (!response.ok || isInvalidTokenPayload(parsed) || !parsed.access_token) {
    throw new AuthExpiredError(
      parsed.error_descrip || "Could not refresh access token."
    );
  }

  return {
    access_token: parsed.access_token,
    expires_in: parsed.expires_in,
  };
}

export function waitForEmbeddedAuth(options?: {
  timeoutMs?: number;
  retryIntervalMs?: number;
  failFastOnError?: boolean;
}): Promise<AuthData> {
  const timeoutMs = options?.timeoutMs ?? 18000;
  const retryIntervalMs = options?.retryIntervalMs ?? 2500;
  const failFastOnError = options?.failFastOnError ?? false;

  return new Promise((resolve, reject) => {
    const odooOrigin = getOdooOrigin();
    if (!odooOrigin) {
      reject(
        new Error(BRANDING.missingEmbeddedOrigin)
      );
      return;
    }

    let settled = false;
    let retryTimer: number | undefined;
    let lastAuthError: Error | null = null;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (retryTimer) {
        window.clearInterval(retryTimer);
      }
      window.removeEventListener("message", onMessage);
    };

    const finishResolve = (authData: AuthData) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(authData);
    };

    const finishReject = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error);
    };

    const onMessage = (event: MessageEvent) => {
      if (!isAllowedOdooOrigin(event.origin)) {
        return;
      }

      const data = event.data;
      if (!data || typeof data !== "object") {
        return;
      }

      if (data.type === AUTH_RESPONSE && data.auth_data?.access_token) {
        finishResolve(data.auth_data);
        return;
      }

      if (data.type === AUTH_ERROR) {
        lastAuthError = new Error(
          formatAuthErrorMessage(
            data.error_descrip || data.error || "Embedded authentication failed."
          )
        );
        if (failFastOnError) {
          finishReject(lastAuthError);
        }
      }
    };

    const postAuthRequest = () => {
      window.parent.postMessage({ type: AUTH_REQUEST }, odooOrigin);
    };

    window.addEventListener("message", onMessage);
    postAuthRequest();
    retryTimer = window.setInterval(postAuthRequest, retryIntervalMs);

    const timeoutId = window.setTimeout(() => {
      finishReject(
        lastAuthError ||
          new Error("Timed out waiting for System session authentication.")
      );
    }, timeoutMs);
  });
}

export function requestEmbeddedAuth(): Promise<AuthData> {
  return waitForEmbeddedAuth();
}

export async function resetEmbeddedAuthSession(): Promise<AuthData> {
  clearAuthData();
  const authData = await requestEmbeddedAuth();
  return completeAuthSession(authData);
}

export async function recoverAuth(): Promise<AuthData | null> {
  if (authRecoveryPromise) {
    return authRecoveryPromise;
  }

  authRecoveryPromise = (async () => {
    const stored = getStoredAuthData();

    if (stored?.refresh_token) {
      try {
        const refreshed = await refreshAccessToken(stored.refresh_token);
        const nextAuth: AuthData = {
          ...stored,
          ...refreshed,
        };
        return completeAuthSession(nextAuth);
      } catch {
        // Fall through to embedded auth or logout.
      }
    }

    if (isEmbeddedMode()) {
      try {
        clearAuthData();
        const authData = await requestEmbeddedAuth();
        return completeAuthSession(authData);
      } catch {
        clearAuthData();
        return null;
      }
    }

    clearAuthData();
    return null;
  })().finally(() => {
    authRecoveryPromise = null;
  });

  return authRecoveryPromise;
}

export async function ensureEmbeddedAuth(): Promise<AuthData> {
  const authData = await requestEmbeddedAuth();
  return completeAuthSession(authData);
}

export async function parseOdooApiResponse<T = unknown>(
  response: Response
): Promise<T> {
  const text = await response.text();
  let parsed: T & { error?: string; error_descrip?: string };

  try {
    parsed = JSON.parse(text);
  } catch {
    if (response.status === 401) {
      throw new AuthExpiredError();
    }
    throw new OdooApiError(
      text?.trim() || "Unexpected server response.",
      "invalid_response",
      response.status
    );
  }

  if (response.status === 401 || isInvalidTokenPayload(parsed)) {
    throw new AuthExpiredError(
      parsed.error_descrip || "Token is expired or invalid!"
    );
  }

  if (!response.ok || parsed.error) {
    throw createOdooApiError(parsed, response.status);
  }

  return parsed;
}

export async function odooApiFetch<T = unknown>(
  url: string,
  accessToken: string,
  init: RequestInit = {},
  retryOnAuthFailure = true
): Promise<T> {
  const headers = {
    ...getOdooApiHeaders(accessToken, "text/html"),
    ...(init.headers as Record<string, string> | undefined),
  };

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
    });
  } catch (error) {
    throw createNetworkError(error);
  }

  try {
    return await parseOdooApiResponse<T>(response);
  } catch (error) {
    if (!retryOnAuthFailure || !(error instanceof AuthExpiredError)) {
      throw error;
    }

    const recovered = await recoverAuth();
    if (!recovered?.access_token) {
      throw error;
    }

    let retryResponse: Response;
    try {
      retryResponse = await fetch(url, {
        ...init,
        headers: {
          ...getOdooApiHeaders(recovered.access_token, "text/html"),
          ...(init.headers as Record<string, string> | undefined),
        },
      });
    } catch (retryError) {
      throw createNetworkError(retryError);
    }

    return parseOdooApiResponse<T>(retryResponse);
  }
}

export function subscribeToAuthUpdates(
  listener: (authData: AuthData | null) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<AuthData | null>;
    listener(customEvent.detail?.access_token ? customEvent.detail : null);
  };

  window.addEventListener(AUTH_DATA_UPDATED_EVENT, handler);
  return () => window.removeEventListener(AUTH_DATA_UPDATED_EVENT, handler);
}
